import { useEffect, useRef } from 'react'
import {
  computeSummaryOffset,
  getSettleTarget,
  getSummaryCollapseStyle,
} from '../utils/collapsibleSummary'

/** Idle time after the last scroll event before a half-hidden summary settles to an edge. */
const SETTLE_DELAY_MS = 140

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

/**
 * iOS large-title style collapsing summary: the summary overlays the top of the scroll area and
 * is tied to the start of the content — it flattens away with the first rows and only comes back
 * when the user scrolls (or pulls) back to the very top. Mid-list scrolling never brings it back.
 * A half-collapsed summary settles to fully shown / hidden once scrolling stops.
 *
 * Everything runs outside React: scroll events are rAF-throttled and write CSS variables on the
 * container — `--summary-h`, `--summary-offset` (sticky header position) and the visual state
 * `--summary-scale`, `--summary-content-opacity`, `--summary-bounce`. Only transform/opacity and
 * the sticky header `top` change, so the scroll area never resizes.
 */
export const useCollapsibleSummary = (enabled: boolean) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const summaryRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    const scroller = scrollRef.current
    const summary = summaryRef.current
    if (!enabled || !container || !scroller || !summary) return

    let height = 0
    let offset = 0
    let frame = 0
    let settleTimer: ReturnType<typeof setTimeout> | undefined

    const apply = () => {
      const { scale, contentOpacity, bounceShift } = getSummaryCollapseStyle(offset, height)
      container.style.setProperty('--summary-offset', `${offset}px`)
      container.style.setProperty('--summary-scale', String(scale))
      container.style.setProperty('--summary-content-opacity', String(contentOpacity))
      container.style.setProperty('--summary-bounce', `${bounceShift}px`)
    }

    const update = () => {
      frame = 0
      offset = computeSummaryOffset(scroller, height)
      apply()
    }

    // offsetHeight is layout height — the scaleY transform does not affect it.
    const measure = () => {
      height = summary.offsetHeight
      container.style.setProperty('--summary-h', `${height}px`)
      update()
    }

    const settle = () => {
      const target = getSettleTarget(offset, height)
      if (target == null) return
      scroller.scrollTo({ top: target, behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
    }

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update)
      clearTimeout(settleTimer)
      settleTimer = setTimeout(settle, SETTLE_DELAY_MS)
    }

    measure()
    scroller.addEventListener('scroll', onScroll, { passive: true })
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    resizeObserver?.observe(summary)

    return () => {
      scroller.removeEventListener('scroll', onScroll)
      resizeObserver?.disconnect()
      cancelAnimationFrame(frame)
      clearTimeout(settleTimer)
      for (const name of [
        '--summary-h',
        '--summary-offset',
        '--summary-scale',
        '--summary-content-opacity',
        '--summary-bounce',
      ]) {
        container.style.removeProperty(name)
      }
    }
  }, [enabled])

  return { containerRef, scrollRef, summaryRef }
}
